import { getKeyByValue } from '../../../components/SelectWidgetType/SelectWidgetType';
import { DimensionTypes, FieldToCheck, LimitState, widgetLimitConfigs } from '../../../constants';
import { isHistoricalQuery } from '../../../CustomDashboardsUtils/utils';
import { AllTypeWidgets, Field, TableSubType } from '../../../types';
import { FieldType } from '../../../types/types';

const METRICS_GROUP_NAME = getKeyByValue(FieldType, FieldType.Measurements);
const getDimsDisabledText = (limit: number) => `You can add up to ${limit} dimensions`;
const getMetricsDisabledText = (limit: number) => `You can add up to ${limit} measurements`;
const getDisabledObj = (disabledTooltip = '') => ({ disabled: true, disabledTooltip });

const isDimSelected = (dims: Field[], field: FieldToCheck) => !!dims.find(({ name }) => name === field.value);
const isMetricSelected = (metrics: Field[], field: FieldToCheck) => !!metrics.find(({ name }) => name === field.value);

const isDim = (field: FieldToCheck) => field.group !== METRICS_GROUP_NAME;
const isMetric = (field: FieldToCheck) => field.group === METRICS_GROUP_NAME;

export type SelectionLimitState = {
  metrics: LimitState;
  dims: LimitState;
};

export const shouldDisableField = ({
  newWidget,
  field,
  fieldType,
}: {
  newWidget: AllTypeWidgets;
  field: FieldToCheck;
  fieldType: DimensionTypes;
}) => {
  const { type } = newWidget;
  const { dims, metrics } = newWidget.requests[0].select;

  if (type === TableSubType.PivotTable) {
    const isRowDimSelected = !!dims.find(({ type: t }) => t === DimensionTypes.Row);
    const isNextFieldColDim = isRowDimSelected || fieldType === DimensionTypes.Col;

    if (field.repeated && isNextFieldColDim && !isDimSelected(dims, field)) {
      return getDisabledObj('Repeated fields cannot be selected as column dimensions');
    }
  }

  const { category } = newWidget;
  const config = widgetLimitConfigs[type] || widgetLimitConfigs[category];

  if (config) {
    if (isMetric(field) && !isMetricSelected(metrics, field)) {
      const limit = config.metrics({ selectedDims: dims });
      if (metrics.length >= limit) {
        return getDisabledObj(getMetricsDisabledText(limit));
      }
    }

    if (isDim(field) && !isDimSelected(dims, field)) {
      if (type === TableSubType.PivotTable) {
        const currentDimType = fieldType;
        const limit = 1;

        const columnDims = dims.filter(d => d.type === DimensionTypes.Col);
        const rowDims = dims.filter(d => d.type === DimensionTypes.Row);
        if (currentDimType === DimensionTypes.Row) {
          if (rowDims.length >= limit) {
            return getDisabledObj(`You can add up to ${limit} row dimension`);
          }
        }
        if (currentDimType === DimensionTypes.Col) {
          if (columnDims.length >= limit) {
            return getDisabledObj(`You can add up to ${limit} column dimension`);
          }
        }
        if (columnDims.length >= limit && rowDims.length >= limit) {
          return getDisabledObj(`You can add up to ${limit} column dimensions and ${limit} row dimensions`);
        }
      } else {
        const isHistorical = isHistoricalQuery(newWidget.requests[0]);
        const limit = config.dims({ selectedMetrics: metrics, isHistorical });
        if (dims.length >= limit) {
          return getDisabledObj(getDimsDisabledText(limit));
        }
        if (config.customDimensionValidation) {
          const error = config.customDimensionValidation({ selectedDims: dims, field });
          if (error) {
            return getDisabledObj(error);
          }
        }
      }
    }
  }

  return { disabled: false, disabledTooltip: '' };
};

export const hasReachedSelectedFieldsLimit = (widget: AllTypeWidgets): SelectionLimitState => {
  const { category, type } = widget;
  const { dims, metrics } = widget.requests[0].select;

  const config = widgetLimitConfigs[type] || widgetLimitConfigs[category];
  if (config) {
    const isHistorical = isHistoricalQuery(widget.requests[0]);
    const metricLimit = config.metrics({ selectedDims: dims });

    const dimensionLimitState = config.getDimensionLimitState
      ? config.getDimensionLimitState({ selectedDims: dims, selectedMetrics: metrics, isHistorical })
      : {
          isReached: dims.length >= config.dims({ selectedMetrics: metrics, isHistorical }),
          message: getDimsDisabledText(config.dims({ selectedMetrics: metrics, isHistorical })),
        };

    return {
      metrics: {
        isReached: metrics.length >= metricLimit,
        message: getMetricsDisabledText(metricLimit),
      },
      dims: dimensionLimitState,
    };
  }

  return {
    metrics: { isReached: false, message: '' },
    dims: { isReached: false, message: '' },
  };
};
